import React, { useState } from "react";
import axios from "api/axios";
import { cloneDeep } from "lodash";
import { TItemsContainer } from "types/items-container";
import { TTargetType } from "types/target-type";
import {
	calculateGrandTotal,
	calculateTaxTotal,
	calculateTotal,
} from "./utils";
import { SubtleIconButton } from "components/button";
import { RiArrowDownSLine, RiArrowUpSLine } from "react-icons/ri";
import ProductsSelect from "components/react-select/products";
import Taxes from "./components/tax/taxes";
import { dollarFormatter, priceCheck } from "utils";
import GroupHeader from "./components/groupHeader";
import { useTranslation } from 'react-i18next';
import ProductRow from "./components/ProductRow";
import { useLineItemContext } from "./context/LineItemContext";
import { useSnackbarContext } from "components/snackbar";
import { FaInfoCircle } from "react-icons/fa";
import TooltippedElement from "components/TooltippedElement";

const API_BASE = `${process.env.REACT_APP_SERVER_URL}/api/v1/company`;

type Product = {
	id: number;
	fk_ingroup_id: number | null;
	group_instance_id: string | null;
	group_name: string;
	root_id: number;
	fk_product_id: number;
	new: boolean;
	quantity: number;
	unit_price: number;
	list_price: number;
	install_price: number;
	root_name: string;
	taxable: boolean;
	ordering?: number;
};

export default function EditLayout(props: {
	data?: TItemsContainer;
	formik: any;
	id: string;
	type: TTargetType;
}) {
	const { t } = useTranslation();
	const tBase = "containers.lineItems.editLayout";
	const tr = (key: string) => t(`${tBase}.${key}`);
	const { showMath, taxableProductTotal, taxTotal, taxes, noTax } = useLineItemContext();

	const { showSnackbar } = useSnackbarContext();

	const [isShowing, setIsShowing] = useState<{ [key: string]: boolean }>({});

	const priceType: "sale" | "install" = props.formik?.values?.price_type || "list";
	const rateLabel = priceType === "sale" ? "unit_price" : "install_price";
	const products = props.formik.values && props.formik.values.products.length > 0 && props.formik.values.products;

	const getDefaultTaxable = (product: any) => {
		const isService = product?.type === "service" || (product?.fk_product_id != null && product?.fk_item_id == null)
		const isPart = product?.type === "part" || (product?.fk_item_id != null && product?.fk_product_id == null)

		if (isService) return props.data?.tax_all_services_default ?? true
		if (isPart) return props.data?.tax_all_parts_default ?? true
		return true
	}

	const deleteProduct = (i: number) => {
		props.formik.setValues((v: any) => {
			let temp = cloneDeep(v);
			temp.products.splice(i, 1);
			return temp;
		});
	};

	const deleteGroup = (groupInstanceId: string) => {
		props.formik.setValues((v: any) => {
			let temp = cloneDeep(v);
			temp.products = temp.products.filter(
				(product: any) => product.group_instance_id !== groupInstanceId
			);
			return temp;
		});
	};

	const moveProduct = (dragIndex: number, hoverIndex: number) => {
		const products = [...props.formik.values.products];
		const draggedProduct = products[dragIndex];
		const hoverProduct = products[hoverIndex];

		const draggedInstanceId = draggedProduct.group_instance_id;
		const hoverInstanceId = hoverProduct.group_instance_id;

		if (draggedInstanceId !== hoverInstanceId) {
			if ((draggedInstanceId && !hoverInstanceId) || (!draggedInstanceId && hoverInstanceId)) {
				return;
			}
		}

		if (draggedInstanceId && hoverInstanceId && draggedInstanceId === hoverInstanceId) {
			const groupProducts = products.filter(p => p.group_instance_id === draggedInstanceId);
			const groupStartIndex = products.findIndex(p => p.group_instance_id === draggedInstanceId);
			
			const draggedRelativeIndex = groupProducts.findIndex(p => p.id === draggedProduct.id);
			const hoverRelativeIndex = groupProducts.findIndex(p => p.id === hoverProduct.id);
			
			groupProducts.splice(draggedRelativeIndex, 1);
			groupProducts.splice(hoverRelativeIndex, 0, draggedProduct);
			
			products.splice(groupStartIndex, groupProducts.length, ...groupProducts);
		} else if (!draggedInstanceId && !hoverInstanceId) {
			products.splice(dragIndex, 1);
			products.splice(hoverIndex, 0, draggedProduct);
		}

		const updatedProducts = products.map((product, index) => ({
			...product,
			ordering: index + 1,
		}));

		props.formik.setFieldValue("products", updatedProducts);
	};

	const handleToggleGroup = (groupInstanceId: string) => {
    setIsShowing((prevState) => ({
        ...prevState,
        [groupInstanceId]: !prevState[groupInstanceId],
    }));
	};
	
	// Sort products by ordering to ensure proper display order
	const sortedProducts = products ? [...products].sort((a, b) => (a.ordering || 0) - (b.ordering || 0)) : [];
	
	const populateProducts = sortedProducts && sortedProducts.map((product: Product, i: number, f: Product[]) => {
		const groupInstanceId = product?.group_instance_id;
	
		const showHeader =
			i === 0 ||
			product.group_instance_id !== f[i - 1].group_instance_id || 
			(!product.fk_ingroup_id &&
				product.root_id !== f[i - 1].root_id &&
				product.fk_product_id !== f[i - 1].fk_product_id) ||
			(product.new === true && !f[i - 1].new);
	
		const cat =
			showHeader &&
			(product.root_name ||
				(product.new && tr("Just Added")) ||
				(groupInstanceId && tr("Group")) ||
				tr("Service"));
	
		if (groupInstanceId) {
			const groupProducts = sortedProducts.filter((p: Product) => p.group_instance_id === groupInstanceId);
	
			if (groupProducts[0].id === product.id) {
				return (
					<React.Fragment key={`group-${groupInstanceId}`}>
						{cat && (
							<div className="col-span-full flex items-center">
								<h3 className="pt-2 pb-1 text-sm font-bold uppercase col-span-full text-slate-500">
									{cat}{" "}
								</h3>
								<SubtleIconButton
									size="small"
									onClick={() => handleToggleGroup(groupInstanceId)}
								>
									{isShowing[groupInstanceId] ? <RiArrowUpSLine /> : <RiArrowDownSLine />}
								</SubtleIconButton>
							</div>
						)}
	
						<GroupHeader
							data={{
								id: product.fk_ingroup_id,
								group_instance_id: groupInstanceId,
								group: true,
								group_name: product.group_name,
								group_products: groupProducts,
							}}
							formik={props.formik}
							rateLabel={rateLabel}
							handleDelete={() => deleteGroup(groupInstanceId)}
							moveProduct={moveProduct}	
							index={sortedProducts.findIndex((p: { id: number; }) => p.id === groupProducts[0].id)}
						/>
	
						{isShowing[groupInstanceId] && groupProducts.map((groupProduct: Product) => {
							const actualIndex = sortedProducts.findIndex((p: { id: number; }) => p.id === groupProduct.id);
							return (
							<ProductRow
								key={`group-product-${groupInstanceId}-${groupProduct.ordering}`} 
								product={groupProduct}
								products={sortedProducts}
								index={actualIndex}
								hideEmpty={props.formik.values.hide_empty}
								deleteProduct={() => deleteProduct(actualIndex)}
								formik={props.formik}
								priceType={priceType}
								rateLabel={rateLabel}
								moveProduct={moveProduct}
								isGroupedProduct={true}
							/>
							);
						})}
					</React.Fragment>
				);
			} else {
				return null;
			}
		} else {
			return (
				<React.Fragment key={`product-${product.ordering}`}>
					{cat && (
						<div className="col-span-full flex items-center">
							<h3 className="pt-2 pb-1 text-sm font-bold uppercase col-span-full text-slate-500">
								{cat}{" "}
							</h3>
						</div>
					)}
	
					<ProductRow
						key={`product-${product.ordering}`}
						product={product}
						products={sortedProducts}
						index={i}
						hideEmpty={props.formik.values.hide_empty}
						deleteProduct={() => deleteProduct(i)}
						formik={props.formik}
						priceType={priceType}
						rateLabel={rateLabel}
						moveProduct={moveProduct}
						isGroupedProduct={false}
					/>
				</React.Fragment>
			);
		}
	});
	
	let total = calculateTotal(props.formik?.values?.products);

	total = {
		list: total.list * 100,
		sale: total.sale * 100,
		install: total.install * 100,
	};

	let afterTaxTotal = calculateTaxTotal(props.formik?.values?.products, taxes, true);

	const grandTotal = calculateGrandTotal(total, afterTaxTotal);

	let taxableSubtotal = props.formik?.values?.products
		.filter((product: Product) => product.taxable)
		.reduce(
			(prev: any, product: Product) => ({
				list: prev.list + product.quantity * +(product.list_price || 0),
				sale: prev.sale + product.quantity * +(product.unit_price || 0),
				install: prev.install + product.quantity * +(product.install_price || 0),
			}),
			{ list: 0, sale: 0, install: 0 }
		);

		taxableSubtotal = {
			list: isNaN(taxableSubtotal.list) ? 0 : taxableSubtotal.list * 100,
			sale: isNaN(taxableSubtotal.sale) ? 0 : taxableSubtotal.sale * 100,
			install: isNaN(taxableSubtotal.install) ? 0 : taxableSubtotal.install * 100,
		};

	const addProduct = async (e: any) => {
		const isGroup = e.type === "group";
		const groupInstanceId = isGroup ? `group_${e.id}_${Date.now()}` : null;

		let newProducts: any[];

		if (isGroup && (!e.children || e.children.length === 0)) {
			try {
				const res = await axios.get(`${API_BASE}/products/group/${e.id}/template`);
				const products = res.data?.products;
				if (products?.length > 0) {
					newProducts = products.map((child: any) => ({
						...child,
						fk_ingroup_id: e.id,
						group_instance_id: groupInstanceId,
						group_name: e.label,
						new: true,
						unit_price: priceCheck(child.unit_price),
						list_price: priceCheck(child.list_price),
						install_price: priceCheck(child.install_price),
						taxable: child.taxable !== undefined ? child.taxable : getDefaultTaxable(child),
					}));
				} else {
					showSnackbar(tr("This group has no items yet; add items on the group page."));
					return;
				}
			} catch {
				showSnackbar(tr("Could not load group items."));
				return;
			}
		} else if (isGroup && e.children?.length > 0) {
			newProducts = e.children.map((child: any) => ({
				...child,
				fk_ingroup_id: e.id,
				group_instance_id: groupInstanceId,
				group_name: e.label,
				new: true,
				unit_price: priceCheck(child.unit_price),
				list_price: priceCheck(child.list_price),
				install_price: priceCheck(child.install_price),
				taxable: getDefaultTaxable(child),
			}));
		} else {
			newProducts = [{
				...e,
				fk_ingroup_id: null,
				group_instance_id: null,
				new: true,
				quantity: 1,
				unit_price: priceCheck(e.unit_price),
				list_price: priceCheck(e.list_price),
				install_price: priceCheck(e.install_price),
				taxable: getDefaultTaxable(e),
			}];
		}

		props.formik.setValues((v: any) => {
			const currentProducts = v.products || [];
			const maxOrdering = currentProducts.length > 0
				? Math.max(...currentProducts.map((p: any) => p.ordering || 0))
				: 0;

			const productsWithOrdering = newProducts.map((product: any, index: number) => ({
				...product,
				ordering: maxOrdering + index + 1,
			}));

			return {
				...v,
				products: [...currentProducts, ...productsWithOrdering],
			};
		});
	};

	const array: ("list" | "install" | "sale")[] = ["list", "sale", "install"];

	const restOfPriceType = array
		.filter((f) => f !== priceType)
		.reduce(
			(prev, f, i, all) =>
				prev +
				`${tr(f.toLocaleUpperCase())}: ${dollarFormatter(grandTotal[f])} ${
					i !== all.length - 1 ? " | " : ""
				}`,
			""
		);

	return (
		<div className="mb-24">
			<div className={`grid 
				${noTax ? "grid-cols-[0.3fr_0.3fr_100px_2fr_5fr_1fr_0.3fr]" : "grid-cols-[0.3fr_0.3fr_100px_2fr_5fr_1fr_1fr_0.3fr]"} 
				gap-3 px-1 border-b border-gray-300 pb-4`}>
				<div className="col-start-3">{tr("Qty")}</div>
				<div>{tr("Label")}</div>
				<div >{tr("Long Description")}</div>
				<div>{tr("Rate")}</div>
				<div>Total</div>
				{!noTax && <div>{tr("Tax")}</div>}
				{populateProducts}
			</div>
			<div className="flex space-x-2 py-4 border-b-[2px] border-black w-100 items-center">
				<div className="pt-2 pb-1 text-sm font-bold uppercase col-span-full text-slate-500">
					{tr("Add Items")}:
				</div>
				<div className="flex flex-1 max-w-[775px]">
					<ProductsSelect onChange={addProduct} />
				</div>
			</div>
			<div className="flex justify-end mt-2">
				{!noTax && (
					<Taxes
						total={total}
						taxableTotal={taxableSubtotal}
						view="edit"
						id={props.id}
						type={props.type}
						priceType={priceType}
					/>
				)}
			</div>
			<div className="flex justify-end">
				<div className="w-[500px] flex justify-between">
					{showMath && taxTotal > 0 ? (
						<div className="text-[1.5em]  flex items-end">
							<div className="relative">
								<div className="text-green-600 mr-1">{dollarFormatter(taxableProductTotal)}</div>
								<div className="absolute top-[-2px] right-[-4px] text-xs">
									<TooltippedElement
										message={"Total of each product (taxable) after fees and discounts applied"}
										placement="top"
										element={<FaInfoCircle className="text-blue-500" size={12} />}
									/>
								</div>
							</div>
							<div className="text-gray-500 mx-2">+</div>
							<div className="relative">
								<div className="text-gray-500">{dollarFormatter(taxTotal)}</div>
								<div className="absolute top-[-2px] right-[-8px] text-xs">
									<TooltippedElement
										message={"Total tax"}
										placement="top"
										element={<FaInfoCircle className="text-blue-500" size={12} />}
									/>
								</div>
							</div>
							<div className="text-gray-500 ml-3">=</div>
						</div>
					) : (
						<div />
					)}
					<div className="">
						<div className="text-[0.8em]">
							{restOfPriceType}
						</div>
						<div className="text-[1.5em] text-right capitalize relative">
							{props.data?.price_type} Total: {dollarFormatter(grandTotal[priceType])}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}